import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { EventsService, IconsOld, NpaElement, NpaOption, ToolService } from '@circe/core';
import { SplitButtonConfig, SplitButtonType } from './split-button.types';
import { NpaDropdownConfig } from '@circe/dropdown';
import { Subject } from 'rxjs';

@Component({
  selector: 'npa-split-button',
  templateUrl: 'split-button.component.html',
  styleUrls: ['split-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitButtonComponent implements OnInit, OnChanges {

  @Input() buttonText: string;
  @Input() hotRender: boolean;
  @Input() config: SplitButtonConfig;
  @Input() menuActions: NpaOption[];

  @Output() mainAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() menuAction: EventEmitter<NpaOption> = new EventEmitter<NpaOption>();

  public id: string;
  public menuConfig: NpaDropdownConfig;
  public menuRelativeElement: NpaElement;
  public small: boolean;
  public disabled: boolean;
  public onlyIcon: boolean;
  public mainHover: boolean;
  public triggerHover: boolean;
  public showDropdown: boolean;
  public type: SplitButtonType;
  public buttonIcon: string;
  public triggerIcon: string;
  public items: NpaOption[];
  public menuKeyboard$: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();

  private _iconClassBase: string;
  private _buttonIconHandler: IconsOld;
  private _triggerIconHandler: IconsOld;
  private _menuIconHandler: IconsOld;
  private _configIsSet: boolean;

  constructor(public ev: EventsService) {
    this.id = ToolService.generateUuid();
    this.items = [];
    this.menuActions = [];
  }

  ngOnInit(): void {
    this.items = [...this.menuActions];
    this._processConfiguration();
    this.menuConfig = {
      elementReference: { type: 'id', name: `npa-split-button-${this.id}`},
      elementRelative: this.menuRelativeElement,
      iconClassBase: this._iconClassBase,
      position: 'bottom right',
      orientation: 'bottom left',
      clickOutsideApply: true,
      width: 'trigger'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hotRender && changes.config?.currentValue) {
      this._processConfiguration();
    }
  }

  public openDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  public selectAction(option: NpaOption): void {
    this.menuAction.emit(option);
  }

  private _processConfiguration(config: SplitButtonConfig = this.config) {
    this._configIsSet = (!!config);

    let _small: boolean = false;
    if (this._configIsSet && 'small' in config) {
      _small = config.small;
    }
    this.small = _small;

    let _onlyIcon: boolean = false;
    if (this._configIsSet && 'onlyIcon' in config) {
      _onlyIcon = config.onlyIcon;
    }
    this.onlyIcon = _onlyIcon;

    let _disabled: boolean = false;
    if (this._configIsSet && 'disabled' in config) {
      _disabled = config.disabled;
    }
    this.disabled = _disabled;

    let _type: SplitButtonType = 'primary';
    if (this._configIsSet && 'type' in config) {
      _type = config.type;
    }
    this.type = _type;

    let _accommodation: 'left' | 'right' = 'left';
    if (this._configIsSet && 'accommodation' in config) {
      _accommodation = config.accommodation;
    }
    this.menuConfig = {
      ...this.menuConfig,
      position: _accommodation === 'left' ? 'bottom right' : 'bottom left',
      orientation: _accommodation === 'left' ? 'bottom left' : 'bottom right'
    };

    if (this._configIsSet && 'menuRelativeElement' in config) {
      this.menuRelativeElement = config.menuRelativeElement;
    }

    let _iconClassBase: string;
    if (this._configIsSet && !!config.iconClassBase) {
      _iconClassBase = config.iconClassBase;
    }
    this._iconClassBase = _iconClassBase;

    this._buttonIconHandler = new IconsOld();
    let _buttonIcon: string = this._buttonIconHandler.classBase;
    if (this._configIsSet && 'icon' in config) {
      if (typeof config.icon === 'boolean') {
        _buttonIcon = config.icon ? _buttonIcon : '';
      } else if (typeof config.icon === 'object') {
        this._buttonIconHandler = new IconsOld(this._iconClassBase);
        _buttonIcon = this._buttonIconHandler.getIcon(config.icon);
      }
    }
    this.buttonIcon = _buttonIcon;

    this._triggerIconHandler = new IconsOld();
    let _triggerIcon: string = this._triggerIconHandler.classBase;
    if (this._configIsSet && 'menuTriggerIcon' in config) {
      if (typeof config.menuTriggerIcon === 'boolean') {
        _triggerIcon = config.menuTriggerIcon ? _triggerIcon : '';
      } else if (typeof config.menuTriggerIcon === 'object') {
        this._triggerIconHandler = new IconsOld(this._iconClassBase);
        _triggerIcon = this._triggerIconHandler.getIcon(config.menuTriggerIcon);
      }
    }
    this.triggerIcon = _triggerIcon;

    let _menuIcon: string = '';
    if (this._configIsSet && 'menuIcon' in config) {
      this._menuIconHandler = new IconsOld(this._iconClassBase);
      _menuIcon = this._menuIconHandler.getIcon(config.menuIcon);
    }
    this.items = this.items.map(d => ({ ...d, icon: { iconLeft: { classes: _menuIcon }, iconRight: null } }));
  }
}
